// UI builder helpers — small DSL for assembling panel-style menus.

import { ABOUT_GREETING, ABOUT_PARAGRAPHS, ABOUT_SIGNOFF } from '../about';

type ElAttrs = {
  class?: string;
  dataset?: Record<string, string>;
  style?: string;
  type?: string;
  min?: string;
  max?: string;
  step?: string;
  value?: string;
  id?: string;
  onclick?: (e: MouseEvent) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: ElAttrs,
  children?: (HTMLElement | string | null | undefined)[],
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v === undefined || v === null) continue;
      if (k === 'class') e.className = v as string;
      else if (k === 'style') e.setAttribute('style', v as string);
      else if (k === 'dataset') {
        for (const [dk, dv] of Object.entries(v as Record<string, string>)) {
          e.dataset[dk] = dv;
        }
      } else if (k.startsWith('on')) {
        (e as unknown as Record<string, unknown>)[k] = v;
      } else if (k === 'value' || k === 'type' || k === 'min' || k === 'max' || k === 'step' || k === 'href' || k === 'src' || k === 'id') {
        e.setAttribute(k, String(v));
      } else {
        try {
          (e as unknown as Record<string, unknown>)[k] = v;
        } catch {
          e.setAttribute(k, String(v));
        }
      }
    }
  }
  if (children) {
    for (const c of children) {
      if (c == null) continue;
      e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    }
  }
  return e;
}

export function toast(text: string): void {
  const t = el('div', { class: 'toast' }, [text]);
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 2200);
}

// ─── Developer letter (first-visit welcome + 'about' link) ──────────

/** The letter body — greeting, paragraphs, signoff. Shared between the
 *  first-visit welcome card (over the loader) and the about modal. */
export function buildAboutLetter(): HTMLElement {
  const wrap = el('div', { class: 'about-letter' });
  wrap.appendChild(el('div', { class: 'welcome-greeting' }, [ABOUT_GREETING]));
  for (const p of ABOUT_PARAGRAPHS) {
    wrap.appendChild(el('p', { class: 'welcome-p' }, [p]));
  }
  wrap.appendChild(el('div', { class: 'welcome-sign' }, [ABOUT_SIGNOFF]));
  return wrap;
}

/** Re-readable letter — opened from the 'about' link in the corner.
 *  Standard dismissable modal (close button / escape / backdrop). */
export function aboutModal(): void {
  const backdrop = el('div', { class: 'confirm-backdrop' });
  const closeBtn = el('button', { type: 'button', class: 'btn primary' }, ['close']);
  const modal = el('div', { class: 'confirm-modal about-modal' }, [
    buildAboutLetter(),
    el('div', { class: 'confirm-modal-row' }, [closeBtn]),
  ]);
  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
  requestAnimationFrame(() => backdrop.classList.add('in'));
  const close = () => {
    backdrop.classList.remove('in');
    setTimeout(() => backdrop.remove(), 200);
    document.removeEventListener('keydown', onKey, true);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.code === 'Escape' || e.code === 'Enter') { e.preventDefault(); close(); }
    e.stopPropagation();
  };
  document.addEventListener('keydown', onKey, true);
  closeBtn.addEventListener('click', close);
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
  closeBtn.focus();
}

/** Toast anchored to the playfield instead of the viewport edge. Used for
 *  level-up / inline notifications so the popup stays near the player's
 *  eye line at any window size instead of drifting to the viewport
 *  bottom on wider monitors. `pos` carries the board rect; `anchor`
 *  picks the side (default 'above'). Anchoring below the board keeps
 *  the popup from colliding with the above-board achievement toast. */
export function playfieldToast(
  text: string,
  pos?: { left: number; top: number; width: number; height?: number },
  anchor: 'above' | 'below' = 'above',
): void {
  const cls = `toast playfield-toast${anchor === 'below' ? ' below' : ''}`;
  const t = el('div', { class: cls }, [text]);
  if (pos) {
    t.style.left = `${Math.round(pos.left + pos.width / 2)}px`;
    t.style.bottom = 'auto';
    if (anchor === 'below' && pos.height != null) {
      // Anchor below the now-playing ticker zone (ticker sits at
      // pos.top + height + 4 and is ~26px tall; +44 clears it). The
      // popup itself grows downward from this anchor and fades out
      // by translating further down.
      t.style.top = `${Math.round(pos.top + pos.height + 44)}px`;
      t.style.transform = 'translate(-50%, 0)';
    } else {
      // Centered above the board, growing upward.
      t.style.top = `${Math.round(pos.top - 28)}px`;
      t.style.transform = 'translate(-50%, -100%)';
    }
  }
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 2200);
}

/** Subtle bottom-left notification for the radio. Stays out of the way
 *  of the playfield, the burger button, the achievement toasts, and the
 *  regular bottom-centered toast. Coalesces — if a new track starts
 *  before the previous toast has faded, the previous one is replaced. */
let nowPlayingNode: HTMLElement | null = null;
let nowPlayingTimer: number | null = null;
export function nowPlayingToast(
  title: string,
  pos?: { left: number; top: number; width: number },
): void {
  if (nowPlayingNode) {
    nowPlayingNode.remove();
    nowPlayingNode = null;
  }
  if (nowPlayingTimer !== null) {
    clearTimeout(nowPlayingTimer);
    nowPlayingTimer = null;
  }
  // Ticker: a thin horizontal strip pinned just under the playfield.
  // Fades in, the content scrolls right-to-left across slowly twice,
  // then the strip fades out. Edges are mask-feathered (see CSS) so
  // text emerges from / dissolves into the strip ends.
  const t = el('div', { class: 'now-playing-toast' }, [
    el('div', { class: 'now-playing-inner' }, [
      icon('now-playing'),
      el('span', { class: 'now-playing-label' }, ['now playing']),
      el('span', { class: 'now-playing-sep' }, ['—']),
      el('span', { class: 'now-playing-title' }, [title]),
    ]),
  ]);
  // Optional positioning override — main.ts passes the player's board
  // viewport rect so the ticker aligns directly under the playfield.
  if (pos) {
    t.style.left = `${pos.left}px`;
    t.style.top = `${pos.top}px`;
    t.style.width = `${pos.width}px`;
    t.style.transform = 'none';  // override the centered default
  }
  document.body.appendChild(t);
  nowPlayingNode = t;
  // Total visible time: 600ms fade-in + 2 × 11s scroll + 600ms fade-out
  // = 23.2s. Unmount slightly after to ensure the fade-out completes.
  nowPlayingTimer = window.setTimeout(() => {
    if (nowPlayingNode === t) {
      t.remove();
      nowPlayingNode = null;
    }
    nowPlayingTimer = null;
  }, 23500);
}

/** Themed confirmation modal — replaces the OS-native `confirm()` so
 *  destructive actions ("reset to defaults", "delete profile", etc.)
 *  use the same visual language as the rest of the UI. Returns a
 *  Promise that resolves to true (confirmed) or false (cancelled). */
export function confirmModal(opts: {
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Tone of the confirm button — 'danger' for destructive actions
   *  (reset/delete), 'primary' for benign confirmations. */
  tone?: 'danger' | 'primary';
}): Promise<boolean> {
  return new Promise((resolve) => {
    const tone = opts.tone ?? 'danger';
    const confirmLabel = opts.confirmLabel ?? (tone === 'danger' ? 'confirm' : 'ok');
    const cancelLabel = opts.cancelLabel ?? 'cancel';
    const backdrop = el('div', { class: 'confirm-backdrop' });
    const modal = el('div', { class: 'confirm-modal' }, [
      el('div', { class: 'confirm-modal-title' }, [opts.title]),
      el('div', { class: 'confirm-modal-body' }, [opts.body]),
      el('div', { class: 'confirm-modal-row' }, [
        el('button', { type: 'button', class: 'btn ghost confirm-cancel' }, [cancelLabel]),
        el('button', { type: 'button', class: `btn confirm-go ${tone === 'danger' ? 'danger' : 'primary'}` }, [confirmLabel]),
      ]),
    ]);
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);
    // Animate in next frame so the CSS transition plays.
    requestAnimationFrame(() => backdrop.classList.add('in'));

    const close = (result: boolean) => {
      backdrop.classList.remove('in');
      setTimeout(() => backdrop.remove(), 200);
      document.removeEventListener('keydown', onKey);
      resolve(result);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Escape') { e.preventDefault(); close(false); }
      else if (e.code === 'Enter') { e.preventDefault(); close(true); }
    };
    document.addEventListener('keydown', onKey);
    (modal.querySelector('.confirm-cancel') as HTMLButtonElement).addEventListener('click', () => close(false));
    (modal.querySelector('.confirm-go') as HTMLButtonElement).addEventListener('click', () => close(true));
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) close(false);
    });
    // Focus the cancel button by default so a stray Enter / Space doesn't
    // accidentally confirm a destructive action.
    (modal.querySelector('.confirm-cancel') as HTMLButtonElement).focus();
  });
}

/** Themed single-button alert modal — for error/status messages that
 *  deserve more presence than a toast (e.g. score submission failures).
 *  Resolves when dismissed. */
export function alertModal(opts: {
  title: string;
  body: string;
  /** Optional dim monospace detail line (raw error codes etc.). */
  detail?: string;
  buttonLabel?: string;
}): Promise<void> {
  return new Promise((resolve) => {
    const backdrop = el('div', { class: 'confirm-backdrop' });
    const okBtn = el('button', { type: 'button', class: 'btn primary' }, [opts.buttonLabel ?? 'ok']);
    const modal = el('div', { class: 'confirm-modal' }, [
      el('div', { class: 'confirm-modal-title' }, [opts.title]),
      el('div', { class: 'confirm-modal-body' }, [opts.body]),
      opts.detail ? el('div', { class: 'alert-modal-detail' }, [opts.detail]) : null,
      el('div', { class: 'confirm-modal-row' }, [okBtn]),
    ]);
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);
    requestAnimationFrame(() => backdrop.classList.add('in'));
    const close = () => {
      backdrop.classList.remove('in');
      setTimeout(() => backdrop.remove(), 200);
      document.removeEventListener('keydown', onKey, true);
      resolve();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Escape' || e.code === 'Enter') { e.preventDefault(); close(); }
      e.stopPropagation();
    };
    document.addEventListener('keydown', onKey, true);
    okBtn.addEventListener('click', close);
    backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
    okBtn.focus();
  });
}

/** Celebration / confirmation popup — icon + title + body in the same
 *  visual language as the feedback confirmation, auto-dismissing after
 *  a beat. Used for score-submission success and similar "it worked"
 *  moments that deserve more presence than a toast. */
export function successModal(opts: {
  iconName: string;
  title: string;
  body: string;
  autoDismissMs?: number;
}): void {
  const backdrop = el('div', { class: 'confirm-backdrop' });
  const okBtn = el('button', { type: 'button', class: 'btn primary' }, ['close']);
  const modal = el('div', { class: 'confirm-modal' }, [
    el('div', { class: 'feedback-confirm' }, [
      el('div', { class: 'feedback-confirm-icon' }, [icon(opts.iconName)]),
      el('div', { class: 'confirm-modal-title' }, [opts.title]),
      el('div', { class: 'confirm-modal-body' }, [opts.body]),
      el('div', { class: 'confirm-modal-row' }, [okBtn]),
    ]),
  ]);
  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
  requestAnimationFrame(() => backdrop.classList.add('in'));
  const close = () => {
    backdrop.classList.remove('in');
    setTimeout(() => backdrop.remove(), 200);
    document.removeEventListener('keydown', onKey, true);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.code === 'Escape' || e.code === 'Enter') { e.preventDefault(); close(); }
    e.stopPropagation();
  };
  document.addEventListener('keydown', onKey, true);
  okBtn.addEventListener('click', close);
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
  okBtn.focus();
  setTimeout(close, opts.autoDismissMs ?? 2800);
}

/** Feedback composer modal. The player writes a message; we append a
 *  system snapshot (shown to them first — nothing leaves the machine
 *  unseen) and POST it to /api/feedback, which stores it and emails
 *  the maintainer. On success the modal swaps to a confirmation and
 *  auto-dismisses; failures render inline (never as a toast, which
 *  would stack under the modal backdrop). */
export interface FeedbackImagePayload { dataB64: string; type: 'image/jpeg' }

export function feedbackModal(opts: {
  /** Resolves the troubleshooting snapshot block. Kicked off on open
   *  so it's ready by submit time. */
  buildSnapshot: () => Promise<string>;
  /** Client-side screenshot compressor (downscale + jpeg). */
  prepareImage: (file: File) => Promise<FeedbackImagePayload | { error: string }>;
  submit: (message: string, snapshot: string, image: FeedbackImagePayload | null) => Promise<{ ok: boolean; error?: string }>;
}): void {
  const backdrop = el('div', { class: 'confirm-backdrop' });
  const textarea = el('textarea', {
    class: 'feedback-textarea',
    placeholder: "e.g. 'found a bug: the hold box stops responding after…' · 'sprint feels too…' · 'feature idea: …'",
    maxlength: '1500',
    rows: '5',
  }) as HTMLTextAreaElement;
  const snapshotPre = el('pre', { class: 'feedback-snapshot' }, ['gathering system info…']);
  const details = el('details', { class: 'feedback-details' }, [
    el('summary', {}, ['system snapshot attached for troubleshooting — click to preview']),
    snapshotPre,
  ]);

  // Screenshot attach: hidden file input + a small row showing state.
  // The hint covers the default capture shortcut per OS.
  const fileInput = el('input', { type: 'file', accept: 'image/*', style: 'display:none' }) as HTMLInputElement;
  const attachBtn = el('button', { type: 'button', class: 'btn ghost feedback-attach-btn' }, ['attach a screenshot']);
  const attachStatus = el('span', { class: 'feedback-attach-status' }, ['']);
  const removeBtn = el('button', { type: 'button', class: 'feedback-attach-remove', title: 'remove screenshot' }, ['×']);
  removeBtn.style.display = 'none';
  const attachRow = el('div', { class: 'feedback-attach-row' }, [attachBtn, attachStatus, removeBtn, fileInput]);
  const attachHint = el('div', { class: 'feedback-attach-hint' }, [
    'capture first: win+shift+s (windows) · cmd+shift+4 (macos) · prtsc (linux, varies by desktop) — then attach the saved image.',
  ]);

  const sendBtn = el('button', { type: 'button', class: 'btn primary' }, ['submit feedback']);
  const cancelBtn = el('button', { type: 'button', class: 'btn ghost' }, ['cancel']);
  // Inline error line — lives INSIDE the modal so failures are always
  // visible (toasts render under the modal backdrop and get lost).
  const errorLine = el('div', { class: 'feedback-error' }, ['']);
  const modal = el('div', { class: 'confirm-modal feedback-modal' }, [
    el('div', { class: 'confirm-modal-title' }, ['send feedback']),
    el('div', { class: 'confirm-modal-body' }, [
      'tell us what broke or what you wish settle did differently.',
    ]),
    textarea,
    attachRow,
    attachHint,
    details,
    errorLine,
    el('div', { class: 'confirm-modal-row' }, [cancelBtn, sendBtn]),
  ]);
  backdrop.appendChild(modal);
  document.body.appendChild(backdrop);
  requestAnimationFrame(() => backdrop.classList.add('in'));

  let snapshot = '';
  opts.buildSnapshot().then((s) => {
    snapshot = s;
    snapshotPre.textContent = s;
  }).catch(() => {
    snapshot = 'snapshot unavailable';
    snapshotPre.textContent = snapshot;
  });

  const close = () => {
    backdrop.classList.remove('in');
    setTimeout(() => backdrop.remove(), 200);
    document.removeEventListener('keydown', onKey, true);
  };
  const onKey = (e: KeyboardEvent) => {
    // Escape closes; everything else is stopped at document-capture so
    // the menu screens' window-level key handlers don't react to
    // typing. Default actions still run, so the textarea gets the
    // characters normally.
    if (e.code === 'Escape') { e.preventDefault(); close(); }
    e.stopPropagation();
  };
  document.addEventListener('keydown', onKey, true);
  cancelBtn.addEventListener('click', close);
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });

  const showError = (text: string) => {
    errorLine.textContent = text;
    errorLine.classList.add('show');
  };

  // Screenshot selection → compress immediately so oversize problems
  // surface before the player hits submit.
  let image: FeedbackImagePayload | null = null;
  attachBtn.addEventListener('click', () => fileInput.click());
  removeBtn.addEventListener('click', () => {
    image = null;
    fileInput.value = '';
    attachStatus.textContent = '';
    removeBtn.style.display = 'none';
  });
  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;
    attachStatus.textContent = 'processing…';
    const result = await opts.prepareImage(file);
    if ('error' in result) {
      image = null;
      attachStatus.textContent = '';
      removeBtn.style.display = 'none';
      showError(result.error);
      return;
    }
    errorLine.classList.remove('show');
    image = result;
    const kb = Math.round((result.dataB64.length * 3) / 4 / 1024);
    attachStatus.textContent = `attached (${kb} kb)`;
    removeBtn.style.display = '';
  });

  let busy = false;
  sendBtn.addEventListener('click', async () => {
    if (busy) return;
    const message = textarea.value.trim();
    if (!message) {
      textarea.focus();
      showError('write a quick message first');
      return;
    }
    busy = true;
    errorLine.classList.remove('show');
    sendBtn.textContent = 'sending…';
    sendBtn.classList.add('busy');
    const res = await opts.submit(message, snapshot, image);
    if (res.ok) {
      // Swap the modal content for the confirmation state, then
      // auto-dismiss after a beat (close button remains for the
      // impatient).
      while (modal.firstChild) modal.removeChild(modal.firstChild);
      const okBtn = el('button', { type: 'button', class: 'btn primary' }, ['close']);
      okBtn.addEventListener('click', close);
      modal.appendChild(el('div', { class: 'feedback-confirm' }, [
        el('div', { class: 'feedback-confirm-icon' }, [icon('octagon-check-solid')]),
        el('div', { class: 'confirm-modal-title' }, ['thanks for your feedback']),
        el('div', { class: 'confirm-modal-body' }, ['your response has been sent!']),
        el('div', { class: 'confirm-modal-row' }, [okBtn]),
      ]));
      okBtn.focus();
      setTimeout(close, 2800);
    } else {
      busy = false;
      sendBtn.textContent = 'submit feedback';
      sendBtn.classList.remove('busy');
      showError(res.error === 'feedback not configured'
        ? "feedback isn't set up on the server yet — try again later"
        : `couldn't send (${res.error ?? 'unknown error'}) — try again in a moment`);
    }
  });
  textarea.focus();
}

/** Insert the geometric corner accents on a panel + mount the version
 *  watermark on the document body (NOT inside the panel — the panel's
 *  backdrop-filter creates a containing block which would trap a
 *  position:fixed descendant and make it scroll/grow with the panel). */
/** SVG icon helper. Lazily fetches `/icons/<name>.svg` the first time
 *  the icon is requested, caches the source string, then inlines it as
 *  child markup. The SVGs use fill="currentColor" so size/color are
 *  controlled via the wrapping element's font-size + color.
 *
 *  Returns the wrapper span synchronously so the caller can place it
 *  immediately; the SVG content pops in once the fetch resolves. */
const iconCache = new Map<string, string>();
const iconPending = new Map<string, Promise<string>>();
async function loadIconSvg(name: string): Promise<string> {
  if (iconCache.has(name)) return iconCache.get(name)!;
  const inflight = iconPending.get(name);
  if (inflight) return inflight;
  const p = fetch(`/icons/${name}.svg`).then(async (r) => {
    if (!r.ok) throw new Error(`icon ${name} not found`);
    const t = await r.text();
    iconCache.set(name, t);
    return t;
  });
  iconPending.set(name, p);
  return p;
}

export function icon(name: string, extraClass = ''): HTMLElement {
  const cls = `icon${extraClass ? ' ' + extraClass : ''}`;
  const wrap = el('span', { class: cls, 'data-icon': name, 'aria-hidden': 'true' });
  loadIconSvg(name)
    .then((svg) => { wrap.innerHTML = svg; })
    .catch(() => { /* missing icon — leave the empty wrapper */ });
  return wrap;
}

/** Preload an explicit list of icons (e.g. all icons used by the menu
 *  on first paint) so they're in the cache before the user navigates
 *  to a screen that uses them. Returns when all fetches resolve. */
export async function preloadIcons(names: string[]): Promise<void> {
  await Promise.all(names.map((n) => loadIconSvg(n).catch(() => null)));
}

export function decoratePanel(panel: HTMLElement, watermark?: string): HTMLElement {
  panel.appendChild(el('div', { class: 'corners' }));
  if (watermark) mountWatermark(watermark);
  return panel;
}

/** Sub-screen title that sits below the brand header. A thin accent
 *  divider line separates the brand strip from the screen-specific h1
 *  (e.g. "settings", "stats", "results"). Use on every screen except
 *  the main menu (which renders the brand header alone). */
export function screenTitle(name: string): HTMLElement {
  const wrap = el('div', { class: 'screen-title-wrap' });
  wrap.appendChild(el('div', { class: 'screen-title-rule' }));
  wrap.appendChild(el('h1', { class: 'screen-title' }, [name]));
  return wrap;
}

/** Optional action fired when the version watermark is clicked.
 *  main.ts registers the changelog navigation here at boot — the
 *  watermark is the deliberately non-descript "what's new" entry. */
let watermarkAction: (() => void) | null = null;
export function setWatermarkAction(cb: (() => void) | null): void {
  watermarkAction = cb;
  const existing = document.getElementById('panel-watermark');
  if (existing) existing.classList.toggle('clickable', !!cb);
}

/** Mount or replace the floating version watermark on the document body.
 *  Positioned fixed to the viewport so it never moves with panel scroll. */
export function mountWatermark(text: string): void {
  const existing = document.getElementById('panel-watermark');
  if (existing) existing.remove();
  const wm = el('div', { id: 'panel-watermark', class: `watermark-fixed${watermarkAction ? ' clickable' : ''}`, title: "what's new" }, [text]);
  wm.addEventListener('click', () => watermarkAction?.());
  document.body.appendChild(wm);
}

/** Remove the floating watermark — called on screen teardown. */
export function unmountWatermark(): void {
  document.getElementById('panel-watermark')?.remove();
}

/** Build a "system header" — small terminal-style status line above a title. */
export function statusLine(parts: Array<string | { label: string; value: string }>): HTMLElement {
  const wrap = el('div', { class: 'subtitle' }, [el('span', { class: 'dot' })]);
  parts.forEach((p, i) => {
    if (i > 0) wrap.appendChild(el('span', { style: 'color: var(--text-dimmer)' }, [' / ']));
    if (typeof p === 'string') {
      wrap.appendChild(el('span', {}, [p]));
    } else {
      wrap.appendChild(el('span', {}, [
        el('span', { style: 'color: var(--text-dimmer); margin-right: 4px' }, [p.label]),
        el('span', {}, [p.value]),
      ]));
    }
  });
  return wrap;
}

/** Per-screen cleanup hooks (e.g., window-level keydown listeners). */
const cleanups: Array<() => void> = [];

export function registerScreenCleanup(fn: () => void): void {
  cleanups.push(fn);
}

// ── Scroll-edge fades for fixed panels ───────────────────────────────
// .panel-fixed .tab-content fades its CONTENT to transparent at the
// top/bottom edges (mask-image, in CSS) ONLY when more content exists
// in that direction. These classes gate the masks: no fade on short
// pages, the bottom fade clears at scroll end so the last row reads
// fully, the top fade appears once anything scrolls out of view. The
// mask is glued to the element box, so it can't park mid-content the
// way the earlier sticky pseudos did after webfont reflow. A
// capture-phase scroll listener gives instant updates; the interval
// covers initial render, fonts settling, collapsible growth, resizes.
function updateScrollFades(tc: HTMLElement): void {
  const surplus = tc.scrollHeight - tc.clientHeight;
  tc.classList.toggle('has-more-below', surplus - tc.scrollTop > 6);
  tc.classList.toggle('has-more-above', tc.scrollTop > 6);
}
document.addEventListener('scroll', (e) => {
  const t = e.target;
  if (t instanceof HTMLElement && t.classList.contains('tab-content')) updateScrollFades(t);
}, true);
window.setInterval(() => {
  document.querySelectorAll<HTMLElement>('.panel-fixed .tab-content').forEach(updateScrollFades);
}, 300);

export function clearScreen(host: HTMLElement): void {
  // Run any prior cleanup hooks synchronously to detach window-level listeners
  // before the next screen wires its own up.
  while (cleanups.length) {
    const fn = cleanups.pop()!;
    try { fn(); } catch { /* swallow */ }
  }
  while (host.firstChild) host.removeChild(host.firstChild);
  // The watermark lives on the body, not the panel — tear it down with the screen.
  unmountWatermark();
}

export interface SliderOpts {
  label: string;
  min: number; max: number; step: number;
  value: number;
  format?: (v: number) => string;
  onChange: (v: number) => void;
}

/** Builds a settings label element. If `tooltip` is provided, a small
 *  "?" button sits next to the label that opens a themed popover on
 *  hover (mouse) or tap (touch). No native browser tooltip — the
 *  themed popover is the only surface so the visual style stays
 *  consistent with the rest of the UI. */
function settingLabel(label: string, tooltip?: string): HTMLElement {
  const wrap = el('label', { class: tooltip ? 'has-tooltip' : '' });
  wrap.appendChild(document.createTextNode(label.toLowerCase()));
  if (tooltip) {
    const help = el('button', {
      type: 'button',
      class: 'setting-help',
      'aria-label': tooltip,
    }, ['?']);

    // Track whether the popover is "pinned" (opened by click, stays until
    // dismissed) vs "transient" (opened by hover, hides on leave). Pinned
    // state survives mouseleave so touch users + keyboard users can read
    // the text without it disappearing under their cursor.
    let pinned = false;
    let hidePop: (() => void) | null = null;

    const showPop = (asPinned: boolean) => {
      if (hidePop) hidePop();
      const pop = el('span', { class: 'setting-help-pop' }, [tooltip]);
      // Mounted on document.body with fixed positioning so the popover
      // can never be clipped by the panel's scroll container — rows near
      // the bottom of the scrollport used to have their tooltips cut off
      // at the edge. Placed below the "?" unless that would leave the
      // viewport, in which case it flips above.
      document.body.appendChild(pop);
      const r = help.getBoundingClientRect();
      const gap = 10;
      pop.style.left = `${Math.round(Math.max(8, Math.min(r.left - 6, window.innerWidth - pop.offsetWidth - 8)))}px`;
      if (r.bottom + gap + pop.offsetHeight > window.innerHeight - 8) {
        pop.classList.add('flip-up');
        pop.style.top = `${Math.round(r.top - gap - pop.offsetHeight)}px`;
      } else {
        pop.style.top = `${Math.round(r.bottom + gap)}px`;
      }
      // Force a reflow + add the 'in' class on the next frame so the
      // CSS transition runs (opacity 0 → 1, translateY).
      requestAnimationFrame(() => pop.classList.add('in'));
      pinned = asPinned;
      const cleanup = () => {
        pop.classList.remove('in');
        // Give the transition time to play before removing from DOM.
        setTimeout(() => pop.remove(), 140);
        document.removeEventListener('pointerdown', onDoc);
        document.removeEventListener('scroll', onScroll, true);
        hidePop = null;
      };
      const onDoc = (ev: MouseEvent) => {
        if (!pop.contains(ev.target as Node) && ev.target !== help) cleanup();
      };
      // A fixed-position popover doesn't track its anchor — dismiss on
      // any scroll instead of drifting away from the "?" button.
      const onScroll = () => { cleanup(); pinned = false; };
      document.addEventListener('scroll', onScroll, true);
      if (asPinned) setTimeout(() => document.addEventListener('pointerdown', onDoc), 0);
      hidePop = cleanup;
    };
    // Body-mounted popovers outlive the panel unless torn down with the
    // screen — make sure navigation never strands one.
    registerScreenCleanup(() => { if (hidePop) hidePop(); });

    help.addEventListener('mouseenter', () => { if (!pinned) showPop(false); });
    help.addEventListener('mouseleave', () => { if (!pinned && hidePop) hidePop(); });
    help.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (pinned) {
        // Clicking a pinned tooltip closes it.
        if (hidePop) hidePop();
        pinned = false;
      } else {
        showPop(true);
      }
    });
    help.addEventListener('focus', () => { if (!pinned) showPop(false); });
    help.addEventListener('blur', () => { if (!pinned && hidePop) hidePop(); });
    wrap.appendChild(help);
  }
  return wrap;
}

export function slider(opts: SliderOpts, tooltip?: string): HTMLElement {
  const fmt = opts.format ?? ((v) => v.toString());
  const valEl = el('div', { class: 'value' }, [fmt(opts.value)]);
  const input = el('input', {
    type: 'range', min: String(opts.min), max: String(opts.max), step: String(opts.step), value: String(opts.value),
  });
  input.addEventListener('input', () => {
    const v = parseFloat(input.value);
    valEl.textContent = fmt(v);
    opts.onChange(v);
  });
  return el('div', { class: 'setting' }, [
    settingLabel(opts.label, tooltip),
    input as unknown as HTMLElement,
    valEl,
  ]);
}

export function toggle(label: string, value: boolean, onChange: (v: boolean) => void, tooltip?: string): HTMLElement {
  const sw = el('div', { class: `toggle ${value ? 'on' : ''}` });
  sw.addEventListener('click', () => {
    sw.classList.toggle('on');
    onChange(sw.classList.contains('on'));
  });
  return el('div', { class: 'setting' }, [
    settingLabel(label, tooltip),
    sw,
    el('div', { class: 'value' }, [' ']),
  ]);
}

/** Themed dropdown built from divs rather than a native <select>. The
 *  native control's open option list is rendered by the OS and ignores
 *  CSS cursor / colour rules, which the rest of the UI carefully
 *  themes. This implementation gives us full control over the popup
 *  styling and keeps the custom cursor visible. */
export function select(
  label: string, options: { value: string; label: string }[], current: string, onChange: (v: string) => void,
  tooltip?: string,
): HTMLElement {
  const labelOf = (v: string) => options.find((o) => o.value === v)?.label ?? v;
  let value = current;
  let isOpen = false;

  // The trigger looks like the old select control: full-width, themed
  // border, a chevron on the right. Click toggles the popup.
  const valueText = document.createElement('span');
  valueText.className = 'themed-select-value';
  valueText.textContent = labelOf(value);
  const chevron = document.createElement('span');
  chevron.className = 'themed-select-chevron';
  chevron.textContent = '▾';
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'themed-select';
  trigger.appendChild(valueText);
  trigger.appendChild(chevron);

  // Popup is a sibling so it can break out of overflow-clipped parents
  // (the panel scrollbar) by switching to absolute positioning on demand.
  const popup = document.createElement('div');
  popup.className = 'themed-select-popup';
  options.forEach((opt) => {
    const optEl = document.createElement('button');
    optEl.type = 'button';
    optEl.className = 'themed-select-option' + (opt.value === value ? ' selected' : '');
    optEl.textContent = opt.label;
    optEl.dataset.value = opt.value;
    optEl.addEventListener('click', (e) => {
      e.stopPropagation();
      value = opt.value;
      valueText.textContent = labelOf(value);
      popup.querySelectorAll('.themed-select-option').forEach((o) => o.classList.remove('selected'));
      optEl.classList.add('selected');
      onChange(value);
      close();
    });
    popup.appendChild(optEl);
  });

  const open = () => {
    if (isOpen) return;
    isOpen = true;
    trigger.classList.add('open');
    popup.classList.add('open');
    setTimeout(() => document.addEventListener('pointerdown', onDoc), 0);
    document.addEventListener('keydown', onKey);
  };
  const close = () => {
    if (!isOpen) return;
    isOpen = false;
    trigger.classList.remove('open');
    popup.classList.remove('open');
    document.removeEventListener('pointerdown', onDoc);
    document.removeEventListener('keydown', onKey);
  };
  const onDoc = (ev: MouseEvent) => {
    if (!popup.contains(ev.target as Node) && !trigger.contains(ev.target as Node)) close();
  };
  const onKey = (ev: KeyboardEvent) => {
    if (ev.code === 'Escape') { ev.preventDefault(); close(); trigger.focus(); }
    else if (ev.code === 'ArrowDown' || ev.code === 'ArrowUp') {
      ev.preventDefault();
      const opts = Array.from(popup.querySelectorAll<HTMLElement>('.themed-select-option'));
      const idx = opts.findIndex((o) => o.dataset.value === value);
      const next = ev.code === 'ArrowDown'
        ? (idx + 1) % opts.length
        : (idx - 1 + opts.length) % opts.length;
      const target = opts[next];
      if (target) target.click();
    }
  };
  trigger.addEventListener('click', () => { if (isOpen) close(); else open(); });

  const wrap = el('div', { class: 'themed-select-wrap' }, [trigger, popup]);
  return el('div', { class: 'setting' }, [
    settingLabel(label, tooltip),
    wrap,
    el('div', { class: 'value' }, [' ']),
  ]);
}
